import React, { FC, useMemo } from 'react';
import { useHistory } from 'react-router';

import {
	EvIssueTypesBySeverityBreakdownChart,
	EvSection,
	EvSpinner
} from '@evinced-private/ui-common';

import {
	WFA_SESSION_ISSUE_TYPE_GRAPH_DAID,
	WFA_SESSION_OVERVIEW_SUMMARY_DAID,
	WFA_SESSION_OVERVIEW_TAB_DAID,
	WFA_SESSION_SEVERITIES_GRAPH_DAID,
	WFA_SESSION_TOP_COMPONENTS_GRAPH_DAID
} from 'data-automation-ids/WfaDashboardAutomationIds';
import { WfaSessionOverviewSkeleton } from 'src/apps/wfa/components/skeletons';
import { useWfaSessionDetailsQuery } from 'src/apps/wfa/hooks/useWfaSessionDetailsQuery';
import { useWfaSessionIssueTypesQuery } from 'src/apps/wfa/hooks/useWfaSessionIssueTypesQuery';
import { useWfaSessionSeveritiesQuery } from 'src/apps/wfa/hooks/useWfaSessionSeveritiesQuery';
import { useWfaSessionSummaryQuery } from 'src/apps/wfa/hooks/useWfaSessionSummaryQuery';
import { useWfaTopSessionComponentsQuery } from 'src/apps/wfa/hooks/useWfaSessionTopComponentsQuery';
import { SummaryPane } from 'src/common/components/ev-summary-pane/EvSummaryPane';
import { TableSkeleton } from 'src/common/components/skeletons/table-skeleton/TableSkeleton';
import { RoutesHelper } from 'src/common/helpers/RoutesHelper';
import { buildFullTableId } from 'src/common/helpers/TableStateHelper';
import { WFA_SESSION_ISSUES_TABLE_ID } from 'src/common/pages/dashboards-container/dashboard_consts';
import { useConfiguration } from 'src/common/providers/configurationProvider/ConfigurationProvider';
import { useTableState } from 'src/common/providers/dashboard/TableStateProvider';
import { SessionViews } from 'src/common/types/WfaSessionViews';

import { ComponentsRaceBarChart } from './charts/race-bar/ComponentsRaceBarChart';
import { BreakdownBySeverityChart } from './charts/severities/BreakdownBySeverityChart';
import { LABELS } from './WfaSessionOverview.labels';

import './WfaSessionOverview.scss';

interface IWfaSessionOverviewProps {
	sessionId: string;
}

export const WfaSessionOverview: FC<IWfaSessionOverviewProps> = ({ sessionId }) => {
	const history = useHistory();
	const { onFilterChange } = useTableState();
	const { configuration } = useConfiguration();
	const isLabelsToggleEnabled = (configuration?.WFA?.TOGGLES as Record<string, boolean>)
		?.ENABLE_PLATFORM_LABELS as boolean;

	const issuesTableIdForSession = useMemo(
		() => buildFullTableId(WFA_SESSION_ISSUES_TABLE_ID, sessionId),
		[sessionId]
	);

	const topComponentsQuery = useWfaTopSessionComponentsQuery({
		sessionId
	});
	const severitiesQuery = useWfaSessionSeveritiesQuery({
		sessionId
	});
	const issuesTypesQuery = useWfaSessionIssueTypesQuery({
		sessionId
	});
	const sessionSummaryQuery = useWfaSessionSummaryQuery({
		sessionId
	});
	const sessionDetailsQuery = useWfaSessionDetailsQuery(sessionId, isLabelsToggleEnabled);

	const isPageLoading =
		topComponentsQuery.isLoading ||
		severitiesQuery.isLoading ||
		issuesTypesQuery.isLoading ||
		sessionSummaryQuery.isLoading ||
		sessionDetailsQuery.isLoading;

	const sessionComponents = useMemo(() => {
		if (!topComponentsQuery?.data) {
			return [];
		}

		return topComponentsQuery?.data?.map((item) => {
			return {
				componentId: item.componentId,
				percentage: Math.ceil(item.percentage)
			};
		});
	}, [topComponentsQuery?.data]);

	const summaryItems = useMemo(() => {
		if (!sessionSummaryQuery.data) {
			return [];
		}
		return [
			{ value: sessionSummaryQuery.data.totalIssues, label: LABELS.TOTAL_ISSUES },
			{ value: sessionSummaryQuery.data.totalComponents, label: LABELS.COMPONENTS },
			{ value: sessionSummaryQuery.data.totalPages, label: LABELS.PAGES_SCANNED },
			{ value: sessionSummaryQuery.data.totalPagesWithIssues, label: LABELS.PAGES_WITH_ISSUES }
		];
	}, [sessionSummaryQuery?.data]);

	const [showInitialSkeleton, setShowInitialSkeleton] = React.useState(isPageLoading);

	React.useEffect(() => {
		if (!isPageLoading && showInitialSkeleton) {
			setShowInitialSkeleton(false);
		}
	}, [isPageLoading, showInitialSkeleton]);

	if (showInitialSkeleton) {
		return <WfaSessionOverviewSkeleton isOverlay />;
	}

	return (
		<EvSection className="wfa-session-overview" dataAutomationId={WFA_SESSION_OVERVIEW_TAB_DAID}>
			{!sessionSummaryQuery.isLoading ? (
				<SummaryPane
					sectionSrLabel={LABELS.SCAN_SUMMARY_SR_LABEL}
					className="custom-summary-pane"
					dataAutomationId={WFA_SESSION_OVERVIEW_SUMMARY_DAID}
					items={summaryItems}
				/>
			) : (
				<EvSpinner />
			)}
			<EvSection dataAutomationId={WFA_SESSION_ISSUE_TYPE_GRAPH_DAID}>
				{issuesTypesQuery.data && !issuesTypesQuery.isLoading ? (
					<EvIssueTypesBySeverityBreakdownChart
						enableFocusBorder={true}
						typesCountBySeverity={issuesTypesQuery.data}
						onBarItemClick={({ type, severity }) => {
							onFilterChange(issuesTableIdForSession, {
								types: [
									{
										value: type,
										label: type
									}
								],
								severities: [
									{
										value: severity,
										label: severity.toString()
									}
								]
							});
							history.push(RoutesHelper.getWfaSessionPath(sessionId, SessionViews.ALL_ISSUES), {
								from: history.location.pathname
							});
						}}
					/>
				) : (
					<TableSkeleton columns={6} rowCount={5} />
				)}
			</EvSection>
			<div className="bottom-row-charts">
				<EvSection
					dataAutomationId={WFA_SESSION_SEVERITIES_GRAPH_DAID}
					skipLinkId="severities-chart-id"
				>
					{severitiesQuery.data && !severitiesQuery.isLoading ? (
						<BreakdownBySeverityChart
							severitiesCount={severitiesQuery.data}
							onBarItemClick={({ severity }) => {
								onFilterChange(issuesTableIdForSession, {
									severities: [
										{
											value: severity,
											label: severity.toString()
										}
									]
								});
								history.push(RoutesHelper.getWfaSessionPath(sessionId, SessionViews.ALL_ISSUES), {
									from: history.location.pathname
								});
							}}
						/>
					) : (
						<EvSpinner />
					)}
				</EvSection>
				<EvSection dataAutomationId={WFA_SESSION_TOP_COMPONENTS_GRAPH_DAID}>
					{topComponentsQuery.data && !topComponentsQuery.isLoading ? (
						<ComponentsRaceBarChart
							sessionComponents={sessionComponents}
							onClick={(componentId) => {
								onFilterChange(issuesTableIdForSession, {
									searchValue: componentId
								});
								history.push(RoutesHelper.getWfaSessionPath(sessionId, SessionViews.ALL_ISSUES), {
									from: history.location.pathname
								});
							}}
						/>
					) : (
						<EvSpinner />
					)}
				</EvSection>
			</div>
		</EvSection>
	);
};
